<script>
  import { format } from 'svelte-i18n';

  import { PUSH_ACTION_COLORS, PUSH_ACTION_TYPES } from '@client/enums';

  import { SelectableList } from '../index.js';

  /**
   * Array of push history entries to display in the list.
   * @type {Array}
   * @default []
   */
  export let history = [];

  /**
   * The ID of the currently selected push entry.
   * @type {string | null}
   * @default null
   */
  export let selectedPushId = null;

  /**
   * Callback function invoked when a push entry is selected.
   * @type {Function}
   * @default () => {}
   */
  export let onSelectPush = () => {};

  /**
   * Indicates whether the history list is currently loading.
   * @type {boolean}
   * @default false
   */
  export let loading = false;

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPushTo = (entry) => {
    return $format(PUSH_ACTION_TYPES[entry.action]) || entry.action || 'Unknown';
  };

  const getPushType = (entry) => {
    const mode = entry.note?.pushMode || 'signature';
    const action = entry.note?.pushAction || 'update';
    return $format('label.PUSH_HISTORY_MODE', { values: { mode, action } });
  };

  const getPushTypeColor = (entry) => {
    const mode = entry.note?.pushMode || 'signature';
    return PUSH_ACTION_COLORS[mode] || PUSH_ACTION_COLORS.signature;
  };

  const getUserCount = (entry) => {
    return entry.note?.sigdata?.data?.length || 1;
  };
</script>

<SelectableList
  items={history}
  selectedId={selectedPushId}
  onSelect={onSelectPush}
  emptyMessage="No push history found"
  {loading}
  let:item
>
  <div class="push-info">
    <div class="push-column push-to">
      <div class="label">Push To</div>
      <div class="value">{getPushTo(item)}</div>
    </div>

    <div class="push-column type">
      <div class="label">Type</div>
      <div>
        <div class="type-badge" style="border-color: #{getPushTypeColor(item)}; color: #{getPushTypeColor(item)};">
          {getPushType(item)}
        </div>
      </div>
    </div>

    <div class="push-column user">
      <div class="label">User</div>
      <div class="value">{item.user}</div>
    </div>

    <div class="push-column date">
      <div class="label">Date</div>
      <div class="value">{formatDate(item.timestamp)}</div>
    </div>

    <div class="push-column users-count">
      <div class="label">Users</div>
      <div class="value">{getUserCount(item)}</div>
    </div>
  </div>
</SelectableList>

<style lang="scss">
  .push-info {
    display: grid;
    grid-template-columns: 0.8fr 1fr 1.4fr 1fr 0.8fr;
    gap: 16px;

    .push-column {
      display: flex;
      flex-direction: column;
      gap: 4px;

      .label {
        font-size: var(--font-size-small);
        font-weight: var(--font-weight-medium);
        color: var(--color-gray-600);
        text-transform: uppercase;
        letter-spacing: 0.5px;
      }

      .value {
        font-size: var(--font-size-base);
        color: var(--color-gray-900);
        word-break: break-word;
      }

      .type-badge {
        border: 1px solid var(--color-gray-500);
        border-radius: var(--border-radius-base);
        color: var(--color-gray-600);
        display: inline-block;
        font-size: var(--font-size-xsmall);
        font-weight: var(--font-weight-medium);
        padding: var(--padding-small) var(--padding-base);
        text-transform: uppercase;
        white-space: nowrap;
      }

      &.users-count {
        text-align: right;
      }
    }
  }

  @media only screen and (max-width: 768px) {
    .push-info {
      grid-template-columns: 1fr 1fr;
      gap: 12px;

      .push-column.users-count {
        text-align: left;
      }
    }
  }
</style>
